import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";

type OncCertificationInfoProps = {
  criterionLabel?: string;
  criterionCode?: string;
};

const OncCertificationInfo = ({
  criterionLabel = "Standardized API for Patient and Population Services",
  criterionCode = "170.315(g)(10)",
}: OncCertificationInfoProps) => {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-[#e3e7f3] bg-white shadow-[0_4px_12px_rgba(4,30,121,0.04)] sm:rounded-[28px]">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px]">
        <div className="relative flex min-w-0 flex-col justify-between gap-6 p-5 sm:p-6 md:p-7 lg:pr-8">
          <div>
            <h2 className="m-0 mb-3 text-xl font-semibold leading-tight sm:text-2xl">
              What is ONC Health IT Certification?
            </h2>
            <div className="space-y-3.5 text-sm leading-normal  sm:mt-5 sm:text-[15px]">
              <p className="m-0 tracking-[0.0175rem] mb-2">
                The Office of the National Coordinator for Health Information
                Technology (ONC) Health IT Certification Program establishes
                standards that enable secure exchange of healthcare information
                across the United States.
              </p>
              <p className="m-0 tracking-[0.0175rem] mb-2">
                AaNeel Connect implements the 170.315(g)(10) Standardized API for
                Patient and Population Services, enabling healthcare
                organizations, providers, payers, and third-party applications to
                securely exchange FHIR-based healthcare data.
              </p>
              <p className="m-0 tracking-[0.0175rem]">
                This documentation helps developers understand the certification
                requirements and implement interoperable APIs using industry
                standards.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 rounded-xl border border-[#e3e7f3] bg-[#f7faff] px-3.5 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-4">
            <div className="flex min-w-0 items-start gap-3 sm:items-center">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-(--desaturateddarkblue) ring-1 ring-[#e3e7f3]">
                <VerifiedOutlinedIcon className="text-lg" />
              </div>
              <div className="min-w-0">
                <p className="m-0 text-[10px] font-semibold uppercase tracking-widest text-(--desaturatedbluecustom) sm:text-xs">
                  Certified Criterion
                </p>
                <p className="m-0 mt-0.5 text-sm font-semibold break-words">
                  {criterionLabel}
                </p>
              </div>
            </div>

            <span className="inline-flex w-max self-start items-center gap-2 rounded-full border border-[#d8deeb] bg-white px-3.5 py-1.5 text-xs font-semibold tracking-wide sm:self-auto sm:shrink-0">
              {criterionCode}
            </span>
          </div>
        </div>

        <aside className="relative flex flex-col items-center justify-center gap-5 px-6 py-8 text-center sm:py-10 lg:px-7 lg:py-8">
          <div className="relative flex flex-col items-center gap-4">
            <svg
              viewBox="0 0 120 140"
              className="h-40 w-[136px] sm:h-44 sm:w-[152px] lg:h-48 lg:w-[164px]"
              aria-hidden
            >
              {/* Ribbon tails */}
              <path
                d="M42 88 L32 128 L48 116 L60 132 L72 116 L88 128 L78 88"
                fill="var(--darkblue)"
                fillOpacity="0.75"
              />
              <path
                d="M48 116 L60 108 L72 116 L60 132 Z"
                fill="var(--darkblue)"
                fillOpacity="0.45"
              />

              {/* Outer hex badge */}
              <path
                d="M60 8 L100 30 L100 74 L60 96 L20 74 L20 30 Z"
                fill="none"
                stroke="#c5cede"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M60 16 L93 34 L93 70 L60 88 L27 70 L27 34 Z"
                fill="none"
                stroke="#d8deeb"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />

              {/* Inner plate */}
              <path
                d="M60 24 L86 38 L86 66 L60 80 L34 66 L34 38 Z"
                fill="var(--darkblue)"
                fillOpacity="0.9"
              />

              <text
                x="60"
                y="50"
                textAnchor="middle"
                fill="white"
                fontSize="12"
                fontWeight="700"
                letterSpacing="2"
              >
                ONC
              </text>
              <text
                x="60"
                y="64"
                textAnchor="middle"
                fill="white"
                fillOpacity="0.85"
                fontSize="6"
                fontWeight="600"
                letterSpacing="1.4"
              >
                CERTIFIED
              </text>
            </svg>

            <div className="relative">
              <p className="m-0 text-[10px] font-semibold uppercase tracking-[0.18em]">
                Criterion
              </p>
              <p className="m-0 mt-1 text-sm font-semibold">
                {criterionCode}
              </p>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default OncCertificationInfo;
